import type { MeInformation } from '@api'
import { createEvent, createStore, sample } from 'effector'

import { LocalStorageKeys } from '@/consts/localStorage.ts'
import { sendCustomAppAction } from '@/modules/desktop/DesktopAppProvider'
import { format } from '@/utils/string'

export const setIsLoggedIn = createEvent<boolean>('set is logged in')
export const $isLoggedIn = createStore(false).on(
  setIsLoggedIn,
  (_, value) => value,
)

export const setAccount = createEvent<Nullable<MeInformation>>('set account')
export const $account = createStore<Nullable<MeInformation>>(null).on(
  setAccount,
  (_, value) => value,
)

export const setIsNSFWOn = createEvent<boolean>('set is nsfw on')
export const $isNSFWOn = createStore<boolean | null>(null)
  .on(setIsNSFWOn, (_, value) => value)
  .on(setAccount, (state, account) => account?.nsfwAlwaysOn || state)

const saveNSFWToStorage = createEvent<{ userId: number; value: boolean }>()

saveNSFWToStorage.watch(({ userId, value }) => {
  localStorage.setItem(
    format(LocalStorageKeys.NSFW_FILTER, userId),
    value.toString(),
  )
})

sample({
  clock: $isNSFWOn,
  source: { account: $account },
  filter: ({ account }, isNSFWOn) => !!account?.userId && isNSFWOn !== null,
  fn: ({ account }, isNSFWOn) => ({
    userId: account!.userId,
    value: isNSFWOn!,
  }),
  target: saveNSFWToStorage,
})

$isNSFWOn.watch((value) => {
  sendCustomAppAction('send-nsfw-filter-to-desktop', {
    value: !!value,
  })

  if (value) {
    document.body.classList.toggle('blur-enabled', value)
  } else {
    document.body.classList.remove('blur-enabled')
  }
})

sample({
  clock: setAccount,
  source: {
    account: $account,
    isNSFWOn: $isNSFWOn,
  },
  filter: ({ account }) => !!account?.userId,
  fn: ({ account, isNSFWOn }) => {
    if (account?.nsfwAlwaysOn) {
      return account.nsfwAlwaysOn
    }

    if (typeof isNSFWOn === 'boolean') {
      return isNSFWOn
    }

    const persistedValue =
      localStorage.getItem(
        format(LocalStorageKeys.NSFW_FILTER, account?.userId),
      ) === 'true'

    return persistedValue
  },
  target: $isNSFWOn,
})

document.addEventListener(
  'set-nsfw-filter',
  (event: Event & { detail?: { value?: boolean } }) => {
    const { value } = event.detail ?? {}

    if (typeof value !== 'boolean') {
      return
    }

    setIsNSFWOn(value)
  },
)
